import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Eye, Handshake, Search, SlidersHorizontal } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompraAgilCard } from "@/components/compra-agil/CompraAgilCard";
import { RecomendacionesPanel } from "@/components/compra-agil/RecomendacionesPanel";
import { ordenarComprasAgiles, type OrdenCompraAgil } from "@/lib/compra-agil-signals";
import { compraAgilService } from "@/services/compra-agil.service";
import type { CATracking, CompraAgil, CompraAgilListResponse } from "@/types";



/**
 * Filtros del listado, persistidos en la URL. Viven acá y no en useState
 * porque si no, volver desde el detalle remonta la página con los valores
 * por defecto y se pierde lo que el usuario tenía aplicado. Además deja el
 * listado filtrado compartible y marcable como favorito.
 */
export type CompraAgilSearch = {
  q?: string;
  estado?: string;
  orden?: OrdenCompraAgil;
  page?: number;
  seguidas?: boolean;
};

const ORDENES_VALIDOS: OrdenCompraAgil[] = ["todas", "cierre", "reciente", "monto"];

function validateSearch(search: Record<string, unknown>): CompraAgilSearch {
  const texto = (key: "q" | "estado") =>
    typeof search[key] === "string" && search[key] ? (search[key] as string) : undefined;
  const orden = ORDENES_VALIDOS.includes(search.orden as OrdenCompraAgil)
    ? (search.orden as OrdenCompraAgil)
    : undefined;
  const page = Number(search.page);
  return {
    q: texto("q"),
    estado: texto("estado"),
    orden,
    page: Number.isFinite(page) && page > 1 ? page : undefined,
    seguidas: search.seguidas === true || search.seguidas === "true" ? true : undefined,
  };
}

export const Route = createFileRoute("/compra-agil/")({
  head: () => ({
    meta: [
      { title: "Compra Ágil — LicitaPro" },
      {
        name: "description",
        content: "Cotizaciones simplificadas de Compra Ágil: productos solicitados, presupuesto y cierre.",
      },
    ],
  }),
  validateSearch,
  component: CompraAgilPage,
});

const ESTADOS = [
  { value: "todos", label: "Todos los estados" },
  { value: "publicada", label: "Publicada" },
  { value: "cerrada", label: "Cerrada" },
  { value: "desierta", label: "Desierta" },
  { value: "cancelada", label: "Cancelada" },
  { value: "proveedor_seleccionado", label: "Proveedor seleccionado" },
];

const ORDENES = [
  { value: "todas", label: "Todas" },
  { value: "cierre", label: "Cierre más próximo" },
  { value: "reciente", label: "Más recientes" },
  { value: "monto", label: "Mayor presupuesto" },
];

const PAGE_SIZE = 25;

function CompraAgilPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const filtros = Route.useSearch();

  // Valores efectivos: lo que dice la URL, o el defecto cuando no dice nada.
  // Por defecto solo lo que todavía admite postulación — "Todos los estados"
  // sigue disponible, pero dejó de ser el punto de entrada: antes esto
  // arrancaba en "todos" + orden ascendente por fecha de cierre, y como hay
  // muchísima Compra Ágil vencida hace meses en la base, lo primero que se
  // veía siempre eran procesos ya cerrados.
  const q = filtros.q ?? "";
  const estado = filtros.estado ?? "publicada";
  const orden: OrdenCompraAgil = filtros.orden ?? "cierre";
  const page = filtros.page ?? 1;
  const soloSeguidas = filtros.seguidas ?? false;

  // El texto del input es local (se escribe letra a letra); solo al enviar el
  // formulario pasa a la URL como filtro `q`.
  const [search, setSearch] = useState(q);

  /** Cambia filtros en la URL. Todo cambio que no sea de página vuelve a la 1. */
  const setFiltros = (cambios: Partial<CompraAgilSearch>) => {
    void navigate({
      search: (prev) => ({ ...prev, page: undefined, ...cambios }),
      replace: true,
    });
  };

  const irAPagina = (destino: number) => {
    void navigate({
      search: (prev) => ({ ...prev, page: destino > 1 ? destino : undefined }),
      replace: true,
    });
  };

  const queryClient = useQueryClient();
  const queryKey = ["compra-agil", { q, estado, page, soloSeguidas }] as const;
  const query = useQuery({
    queryKey,
    queryFn: () =>
      compraAgilService.list({
        q: q || undefined,
        estado: estado === "todos" ? undefined : estado,
        soloTrackeadas: soloSeguidas || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const handleTrackingChange = (id: string, tracking: CATracking) => {
    queryClient.setQueryData<CompraAgilListResponse>(queryKey, (prev) =>
      prev ? { ...prev, items: prev.items.map((i) => (i.id === id ? { ...i, tracking } : i)) } : prev,
    );
  };

  const total = query.data?.total ?? 0;

  // El `?? []` va dentro del useMemo: afuera crea un array nuevo en cada
  // render y el memo no memoriza nada.
  const { items, demandaPromedio } = useMemo(() => {
    const rawItems = query.data?.items ?? [];
    const sorted = ordenarComprasAgiles(rawItems as CompraAgil[], orden);
    const avg =
      rawItems.length > 0
        ? rawItems.reduce((acc, i) => acc + (i.totalOfertasRecibidas ?? 0), 0) / rawItems.length
        : 0;
    return { items: sorted, demandaPromedio: avg };
  }, [query.data?.items, orden]);

  return (
    <AppShell
      title="Compra Ágil"
      subtitle={`${total} resultados · Fuente: Mercado Público (API Compra Ágil v2)`}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <form
              className="relative flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                setFiltros({ q: search.trim() || undefined });
              }}
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, producto o código…"
                className="pl-9"
              />
            </form>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={soloSeguidas ? "default" : "outline"}
                aria-pressed={soloSeguidas}
                onClick={() => setFiltros({ seguidas: soloSeguidas ? undefined : true })}
              >
                <Eye className="size-4" />
                Mis seguidas
              </Button>
              <Select
                value={orden}
                onValueChange={(v) => setFiltros({ orden: v as OrdenCompraAgil })}
              >
                <SelectTrigger className="w-44" aria-label="Ordenar por">
                  <SlidersHorizontal className="size-4 text-muted-foreground mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDENES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={estado} onValueChange={(v) => setFiltros({ estado: v })}>
                <SelectTrigger className="w-44" aria-label="Estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {query.isLoading ? (
            <div className="grid gap-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-40 w-full rounded-xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={soloSeguidas ? Eye : Handshake}
              title={soloSeguidas ? "Sin Compras Ágiles en seguimiento" : "Sin Compras Ágiles"}
              description={
                soloSeguidas
                  ? "Aún no marcas ninguna Compra Ágil como seguida. Ábrela y presiona \"Seguir esta Compra Ágil\"."
                  : "No hay procesos que coincidan con los filtros seleccionados."
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4" data-testid="compra-agil-feed">
                {items.map((ca) => (
                  <CompraAgilCard
                    key={ca.id}
                    item={ca as CompraAgil}
                    demandaPromedio={demandaPromedio}
                    onTrackingChange={handleTrackingChange}
                  />
                ))}
              </div>

              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-6 text-sm text-muted-foreground">
                  <span>
                    Página {page} de {Math.ceil(total / PAGE_SIZE)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => irAPagina(page - 1)}
                      className="px-3 py-1 rounded-md border border-border disabled:opacity-40"
                    >
                      Anterior
                    </button>
                    <button
                      disabled={page >= Math.ceil(total / PAGE_SIZE)}
                      onClick={() => irAPagina(page + 1)}
                      className="px-3 py-1 rounded-md border border-border disabled:opacity-40"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <aside className="w-full lg:w-80 xl:w-96 shrink-0" data-testid="recomendaciones-panel">
          <div className="lg:sticky lg:top-4">
            <RecomendacionesPanel items={items} maxItems={3} />
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
